/**
 * Contrato da linha do tempo consolidada (E84.F4, ADR-0075 §3 emenda m).
 *
 * Espelha apps/api/types/exame_eletronico_jornada.py. Módulo próprio pela
 * mesma razão dos metadados e do vínculo: o contrato do exame já passa do teto
 * de 400 linhas do handbook §7, e este eixo cresce por conta própria. Quem
 * importar estes tipos de `lib/exame_eletronico` continua importando — a
 * re-exportação de lá é o contrato público.
 *
 * Uma única lista de instantes com a fonte ao lado — protocolo, comprovante,
 * arquivo, assinatura embutida, carimbo do tempo, custódia — ordenado pelo
 * instante comparável, e quatro itens que confrontam as fontes entre si. Nada
 * aqui é contradição verificada: a linha de status e a hora reportada pelo
 * signatário são declarações, e a custódia contradiz uma declaração que pode
 * ser a errada; um `nao_confere` vira indicador de alerta médio, nunca rebaixa
 * o grau a inconsistente. Não há ordem esperada entre fontes.
 */
import { ptBR } from '@/i18n/pt-BR';
import type { ItemVerificacaoData } from '@/lib/exame_eletronico';
import type { TipoEventoProtocolo } from '@/lib/exame_eletronico_protocolo';

export type FonteDoInstante =
  | 'protocolo'
  | 'instrumento'
  | 'comprovante'
  | 'registro'
  | 'arquivo'
  | 'assinatura_embutida'
  | 'carimbo_do_tempo'
  | 'custodia';

/** Ordem de desempate entre fontes no mesmo instante — a mesma do backend. */
export const FONTES_DO_INSTANTE: readonly FonteDoInstante[] = [
  'protocolo',
  'instrumento',
  'comprovante',
  // E156.F1 (GUS-156, ADR-0106): o registro da jornada que o banco entrega em planilha ou log.
  'registro',
  'arquivo',
  'assinatura_embutida',
  'carimbo_do_tempo',
  'custodia',
];

/**
 * O que o instante é: os sete tipos de evento do protocolo, o instante de cada fonte, ou
 * (E156.F1) o papel do evento registrado que não é tipo do protocolo.
 */
export type RotuloDoInstante =
  | TipoEventoProtocolo
  | 'carimbo_do_instrumento'
  | 'registro_da_assinatura'
  | 'criacao_do_arquivo'
  | 'modificacao_do_arquivo'
  | 'hora_da_assinatura'
  | 'carimbo_do_tempo'
  | 'entrada_na_custodia'
  | 'disponibilizacao'
  | 'captura_da_foto'
  | 'aprovacao_do_banco'
  | 'geracao_do_arquivo'
  | 'evento_registrado'
  | 'ticks_do_nome_do_arquivo';

export const ROTULOS_FORA_DO_PROTOCOLO: readonly Exclude<RotuloDoInstante, TipoEventoProtocolo>[] =
  [
    'carimbo_do_instrumento',
    'registro_da_assinatura',
    'criacao_do_arquivo',
    'modificacao_do_arquivo',
    'hora_da_assinatura',
    'carimbo_do_tempo',
    'entrada_na_custodia',
    'disponibilizacao',
    'captura_da_foto',
    'aprovacao_do_banco',
    'geracao_do_arquivo',
    'evento_registrado',
    'ticks_do_nome_do_arquivo',
  ];

/** As quatro chaves do eixo, na ordem em que o quadro e o laudo as mostram. */
export type ChaveJornada =
  | 'cronologia_formato_de_data'
  | 'cronologia_registro_da_assinatura'
  | 'cronologia_assinatura_embutida'
  | 'cronologia_custodia';

export const CHAVES_JORNADA: readonly ChaveJornada[] = [
  'cronologia_formato_de_data',
  'cronologia_registro_da_assinatura',
  'cronologia_assinatura_embutida',
  'cronologia_custodia',
];

/**
 * Um instante da jornada, com a fonte ao lado. `em` é o instante comparável
 * (com fuso — presumido quando a fonte não o declarou, e `fuso_presumido` o
 * diz); `lido_em_mes_dia` marca a data que o comprovante imprimiu em mês/dia
 * e a âncora corrigiu; `fora_de_ordem` e `utc_local_incoerentes` são os selos
 * da trilha da E74, copiados dos eventos do protocolo.
 */
export interface InstanteDaJornadaData {
  fonte: FonteDoInstante;
  rotulo: RotuloDoInstante;
  em?: string | null;
  em_local?: string | null;
  fuso_presumido: boolean;
  lido_em_mes_dia: boolean;
  fora_de_ordem: boolean;
  utc_local_incoerentes: boolean;
  /** E106.F2 — o rótulo impresso ("Carimbo do instrumento (CCB)"). */
  descricao?: string | null;
  /** E114.F1 (R-20) — a fonte imprime só hora e minuto. */
  sem_segundos?: boolean;
  /** E114.F1 (R-20) — a precisão que a fonte declara ("500 ms" no carimbo do tempo). */
  precisao?: string | null;
  /** E156.F1 — o que o instante carrega quando a fonte é `registro`. */
  registro?: RegistroDoInstanteData | null;
}

/** E156.F1 (ADR-0106 §2.4–§2.6) — a linha do registro da jornada que virou instante. */
export interface RegistroDoInstanteData {
  evento_literal: string;
  papel: PapelDoEvento;
  identificadores: IdentificadoresDoRegistroData;
  instante_literal: string;
  fuso: 'declarado' | 'nao_informado';
  fuso_declarado?: string | null;
  precisao: 'segundo' | 'minuto' | 'dia';
  data_ambigua: boolean;
  origem: OrigemDoRegistroData;
  vinculo: 'identificador' | 'indicio' | 'sem_identificador';
  equivalente_a?: 'protocolo' | null;
}

export type PapelDoEvento =
  | 'envio_de_notificacao'
  | 'disponibilizacao'
  | 'visualizacao'
  | 'autenticacao'
  | 'aceite'
  | 'prova_de_vida'
  | 'captura_da_foto'
  | 'assinatura'
  | 'aprovacao_do_banco'
  | 'geracao_do_arquivo'
  | 'pagamento'
  | 'outro';

export interface IdentificadoresDoRegistroData {
  ccb?: string | null;
  proposta?: string | null;
  envelope?: string | null;
  sessao?: string | null;
}

export interface OrigemDoRegistroData {
  arquivo: string;
  aba?: string | null;
  linha: number;
  coluna?: string | null;
}

/** E156.F1 — o resumo de cada arquivo de registro lido (ou não) para a peça. */
export interface RegistroNaJornadaData {
  arquivo: string;
  sha256: string;
  formato: 'xlsx' | 'csv' | 'json' | 'texto' | 'xls';
  eventos_lidos: number;
  eventos_da_peca: number;
  eventos_sem_identificador: number;
  linhas_sem_leitura: number;
  /** E156.F1.C02 — das linhas sem leitura, as que declaram fuso fora da faixa. */
  linhas_com_fuso_invalido?: number;
  motivo_sem_leitura?:
    | 'formato_sem_leitor'
    | 'sem_coluna_de_instante'
    | 'sem_linhas'
    | 'ilegivel'
    | null;
}

/** E156.F2 (ADR-0106 §2.7) — os ticks .NET do nome de um arquivo do caso, sem fuso, com o limite. */
export interface TicksDoNomeData {
  arquivo: string;
  literal: string;
  instante_local: string;
  unidade: string;
  granularidade: string;
  truncamento: string;
  vinculo?: 'indicio' | null;
  descricao_do_vinculo?: string | null;
}

/** E156.F3 (ADR-0106 §2.7–§2.8) — o par registrado que a régua de leitura mediu, com a origem das pontas. */
export interface LeituraDaJornadaData {
  inicio: RotuloDoInstante;
  inicio_em: string;
  aceite_em: string;
  segundos: number;
  /** E156.F2.C01 — o maior intervalo admitido quando uma ponta não traz os segundos ou a hora. */
  segundos_ate?: number | null;
  apressada: boolean;
  origem_do_inicio: OrigemDoRegistroData;
  origem_do_aceite: OrigemDoRegistroData;
}

/** E156.F1 — a linha sem identificador: fora da linha do tempo, dúvida para a operação. */
export interface RegistroSemVinculoData {
  evento_literal: string;
  instante_literal: string;
  origem: OrigemDoRegistroData;
}

/**
 * O estágio pronto: a lista ordenada, os quatro itens e a contagem das datas
 * ambíguas do comprovante que a âncora resolveu ou deixou por conferir. No
 * resultado do exame o campo inteiro é nulo para o exame verificado ANTES da
 * Fase existir.
 */
export interface JornadaApuradaData {
  instantes: InstanteDaJornadaData[];
  itens: ItemVerificacaoData[];
  ambiguidades_resolvidas: number;
  ambiguidades_pendentes: number;
  /** E156.F1 — ausentes no exame verificado antes da Fase. */
  registros?: RegistroNaJornadaData[];
  registros_sem_vinculo?: RegistroSemVinculoData[];
  /** E156.F2 — os ticks dos nomes dos arquivos do caso. */
  ticks?: TicksDoNomeData[];
  /** E156.F3 — os pares registrados que a régua de leitura mediu. */
  leituras?: LeituraDaJornadaData[];
}

const tj = ptBR.exameEletronico.quadro.jornada;

/** Rótulo do instante: o do evento do protocolo, ou o da fonte. */
export function rotuloDoInstante(rotulo: RotuloDoInstante): string {
  const eventos: Record<string, string> = ptBR.exameEletronico.form.eventoTipoOpcoes;
  const demais: Record<string, string> = tj.rotulos;
  return demais[rotulo] ?? eventos[rotulo] ?? rotulo;
}

/**
 * E168.F2.C01 (QA de 24/10): o nome do evento como o PDF o imprime — o literal do registro, o rótulo
 * impresso pela fonte ("Contratado em") e, só na falta deles, o rótulo genérico. "Aceite" no lugar de
 * "Contratado em" chamava de aceite do titular um evento do banco.
 */
export function nomeDoInstante(instante: InstanteDaJornadaData): string {
  return (
    instante.registro?.evento_literal ?? instante.descricao ?? rotuloDoInstante(instante.rotulo)
  );
}

export function rotuloDaFonte(fonte: FonteDoInstante): string {
  return tj.fontes[fonte];
}

/** E156.F3 — a origem do registro como a tela imprime: "historico.xlsx, Eventos, linha 6". */
export function origemDoRegistro(origem: OrigemDoRegistroData): string {
  const partes = [origem.arquivo, origem.aba ?? '', `${tj.origem.linha} ${origem.linha}`];
  return partes.filter((p) => p !== '').join(', ');
}

/**
 * E156.F3 — o instante sem fuso como consta ("2024-03-15T10:21:41" vira "15/03/2024 10:21:41"),
 * sem passar por `Date`: um horário sem fuso não tem posição e não deve ganhar uma.
 */
export function instanteLocalComoConsta(iso: string): string {
  const [data, hora = ''] = iso.split('T');
  const [ano, mes, dia] = (data ?? '').split('-');
  if (!ano || !mes || !dia) return iso;
  return `${dia}/${mes}/${ano}${hora ? ` ${hora}` : ''}`;
}
